import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useAuth } from "@/components/AuthProvider";
import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { useSidebar } from "@/components/SidebarContext";

const PUBLIC_ROUTES = new Set(["/signin", "/set-password"]);

export function PlatformAdminShell({ children }: { children: ReactNode }) {
  const pathname = useLocation().pathname;
  const { hydrated, user } = useAuth();
  const { collapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateViewportMode = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);
    return () => window.removeEventListener("resize", updateViewportMode);
  }, []);

  if (PUBLIC_ROUTES.has(pathname)) {
    return <>{children}</>;
  }

  if (!hydrated || !user) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-1/2 focus:top-4 focus:z-[200] focus:-translate-x-1/2 focus:rounded-xl focus:bg-primary focus:px-4 focus:py-2 focus:font-semibold focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to main content
      </a>

      <div className="fixed inset-y-0 left-0 z-40 hidden lg:block">
        <Sidebar appScope="platform-admin" />
      </div>

      {isMobile && sidebarOpen ? (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="h-full w-72" onClick={(event) => event.stopPropagation()}>
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} appScope="platform-admin" />
          </div>
        </div>
      ) : null}

      <div className={collapsed ? "lg:ml-24" : "lg:ml-72"}>
        <Topbar
          onMenuClick={() => setSidebarOpen((current) => !current)}
          collapsed={collapsed}
          appScope="platform-admin"
        />
        <main id="main-content" className="px-4 pb-20 pt-24 sm:px-6 lg:px-10 lg:pb-8">
          <div className="mx-auto w-full max-w-[1500px]">
            <Breadcrumbs className="mb-4" />
            {children}
          </div>
        </main>
      </div>

      <MobileBottomNav appScope="platform-admin" onMoreClick={() => setSidebarOpen(true)} />
    </div>
  );
}
